/**
 * StockEntryHeaderSection — bespoke Thai-SME-friendly overview card for a
 * Stock Entry doc. Replaces the generic form grid with an iPad-Settings-style
 * summary: status pill, action-type icon, warehouse flow, items/date footer.
 *
 * Read-only in this slice — inline editing stays in the other sections.
 * Follows the same visual language as /settings → Appearance (Plan 146):
 *   - uppercase pl-4 section header
 *   - CARD token from design-tokens.ts
 *   - flex items-center justify-between rows
 *
 * Status semantics (ERPNext Stock Entry lifecycle):
 *   - docstatus 0 → Draft
 *   - docstatus 1 → Submitted
 *   - docstatus 2 → Cancelled
 */

import { ArrowRight, Calendar, Package, Warehouse } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/i18n/useLanguage";
import {
  STOCK_ACTIONS,
  type StockActionConfig,
  type WarehouseMode,
} from "@/features/common/doc-dashboard/stockActionsConfig";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

const i18n: Record<string, { en: string; th: string }> = {
  overview: { en: "Overview", th: "ภาพรวม" },
  status_draft: { en: "Draft", th: "ร่าง" },
  status_submitted: { en: "Submitted", th: "ส่งยืนยันแล้ว" },
  status_cancelled: { en: "Cancelled", th: "ยกเลิกแล้ว" },
  items_count: { en: "items", th: "รายการ" },
  posted_on: { en: "Posted", th: "บันทึกเมื่อ" },
  no_type: { en: "No type selected", th: "ยังไม่ได้เลือกประเภท" },
  no_warehouse: { en: "No warehouse", th: "ยังไม่ได้เลือกคลัง" },
};

function formatDate(value: string | undefined, language: "en" | "th"): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString(language === "th" ? "th-TH" : "en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function getStatusMeta(docstatus: number, t: (k: string) => string) {
  if (docstatus === 1) {
    return {
      label: t("status_submitted"),
      className:
        "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    };
  }
  if (docstatus === 2) {
    return {
      label: t("status_cancelled"),
      className:
        "bg-destructive/10 text-destructive dark:bg-destructive/20",
    };
  }
  return {
    label: t("status_draft"),
    className:
      "bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300",
  };
}

function getActionConfig(stockEntryType: string | undefined): StockActionConfig | undefined {
  if (!stockEntryType) return undefined;
  return STOCK_ACTIONS.find(
    (a) => a.kind === "stock_entry_quick_action" && a.stockEntryType === stockEntryType,
  );
}

interface WarehouseFlowProps {
  mode: WarehouseMode | undefined;
  fromWarehouse: string;
  toWarehouse: string;
  emptyLabel: string;
}

function WarehouseFlow({ mode, fromWarehouse, toWarehouse, emptyLabel }: WarehouseFlowProps) {
  const chip = (value: string) => (
    <div className="flex items-center gap-2 rounded-xl bg-muted px-3 py-2 min-w-0">
      <Warehouse className="h-4 w-4 text-muted-foreground shrink-0" />
      <span className="text-sm font-medium text-foreground truncate">
        {value || emptyLabel}
      </span>
    </div>
  );

  // "to" only (Material Receipt) — single destination
  if (mode === "to") {
    return <div className="flex-1 min-w-0">{chip(toWarehouse)}</div>;
  }
  // "from" only (Material Issue) — single source
  if (mode === "from") {
    return <div className="flex-1 min-w-0">{chip(fromWarehouse)}</div>;
  }
  // "from_to" — source → destination (Transfer / Manufacture / Repack)
  return (
    <div className="flex items-center gap-2 flex-1 min-w-0">
      <div className="flex-1 min-w-0">{chip(fromWarehouse)}</div>
      <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">{chip(toWarehouse)}</div>
    </div>
  );
}

export function StockEntryHeaderSection({ formData }: ShopifySectionWidgetProps) {
  const { language } = useLanguage();
  const t = (key: string) => {
    const entry = i18n[key];
    return entry ? (language === "th" ? entry.th : entry.en) : key;
  };

  const docstatus = Number(formData.docstatus ?? 0);
  const stockEntryType = (formData.stock_entry_type as string) || "";
  const fromWarehouse = (formData.from_warehouse as string) || "";
  const toWarehouse = (formData.to_warehouse as string) || "";
  const postingDate = (formData.posting_date as string) || "";
  const items = Array.isArray(formData.items) ? formData.items : [];

  const status = getStatusMeta(docstatus, t);
  const action = getActionConfig(stockEntryType);
  const TypeIcon = action?.icon ?? Package;
  const typeLabel = stockEntryType || t("no_type");

  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">
        {t("overview")}
      </h3>
      <Card className={CARD}>
        <CardContent className="p-5 space-y-4">
          {/* Row 1: type icon + label, status pill right */}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 shrink-0">
                <TypeIcon className="h-5 w-5 text-primary" />
              </div>
              <div className="text-sm font-semibold text-foreground truncate">
                {typeLabel}
              </div>
            </div>
            <Badge className={`shrink-0 px-2.5 py-0.5 ${status.className}`} variant="outline">
              {status.label}
            </Badge>
          </div>

          {/* Row 2: warehouse flow (only if a type is picked) */}
          {action ? (
            <WarehouseFlow
              mode={action.warehouseMode}
              fromWarehouse={fromWarehouse}
              toWarehouse={toWarehouse}
              emptyLabel={t("no_warehouse")}
            />
          ) : null}

          {/* Row 3: items count + posting date footer */}
          <div className="flex items-center justify-between pt-2 border-t border-border/40">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Package className="h-3.5 w-3.5" />
              <span>
                {items.length} {t("items_count")}
              </span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Calendar className="h-3.5 w-3.5" />
              <span>
                {t("posted_on")} {formatDate(postingDate, language === "th" ? "th" : "en")}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
